import { useMemo } from 'react';
import { useGetDraftRosterMembersForIncident } from 'components/IAP/hooks/incidentRoster/useGetDraftRosterMembersForIncident';
import { useResourcesInIncident } from 'components/IAP/hooks/resourceInIncidentHooks';
import {
  useGetPositionsForIncident,
  useGetRosterForIncident,
  useGetSeatResourcesForRoster,
} from 'hooks/incident/useRoster';

export type RosterResourceKind =
  | 'personnel'
  | 'position'
  | 'resourcePosition'
  | 'seatResource';

export const ROSTER_RESOURCE_KIND_LABELS: Record<RosterResourceKind, string> = {
  personnel: 'Personnel',
  position: 'Position',
  resourcePosition: 'Resource',
  seatResource: 'Seat resource',
};

/** Shown where a person would be, for a seat nobody is sitting in. */
export const UNFILLED_SEAT_LABEL = 'Unassigned';

export type RosterResourceOption = {
  key: string;
  /** Person name, or the resource's name. */
  label: string;
  positionName: string | null;
  /** Contact details for people, resource kind for resources. */
  detail: string | null;
  kind: RosterResourceKind;
  /**
   * True when the entry exists only on the draft roster. Those seats have no
   * `ics_resource_in_incident` row yet, so they cannot be planned onto a card
   * until the operational period advances.
   */
  onDraftOnly: boolean;
  /** `ics_resource_in_incident.id`, or null when there is nothing to reference. */
  resourceInIncidentId: string | null;
  /**
   * Set on the rows that stand for a seat rather than an occupant. A seat with
   * no resource yet is still plannable: the picker mints one for it on submit,
   * which is the whole point of offering positions.
   */
  positionId: string | null;
};

const KIND_ORDER: Record<RosterResourceKind, number> = {
  personnel: 0,
  position: 1,
  resourcePosition: 2,
  seatResource: 3,
};

const byKindThenLabel = (a: RosterResourceOption, b: RosterResourceOption) =>
  KIND_ORDER[a.kind] - KIND_ORDER[b.kind] ||
  a.label.localeCompare(b.label) ||
  (a.positionName ?? '').localeCompare(b.positionName ?? '');

/**
 * Everything on the incident roster that an ICS-204 could plan, as one list.
 *
 * A card's planned resources are `ics_resource_in_incident` ids, so each roster
 * entry has to be resolved back to such a row: seat assignments through
 * `position_assignment_id`, attached resources through `resource_set_id`.
 * Matching on the resource's name would be ambiguous whenever two people hold
 * the same position.
 *
 * Positions appear alongside their occupants so a card can name the seat rather
 * than the person in it, which is also the only way to plan a seat that is
 * still empty. Those have no resource row until the picker mints one, so they
 * carry a `positionId` instead.
 *
 * Draft-roster seats deliberately stay in the list even though they cannot be
 * resolved — `assignMemberToPositionInRoster` only creates the resource row for
 * non-draft assignments — so the picker can explain why they are unavailable
 * rather than silently omitting people the user can see on the draft roster.
 */
const useRosterResourceOptions = () => {
  const { data: activeRoster = [], isPending: activePending } =
    useGetRosterForIncident();
  const { data: draftRoster = [], isPending: draftPending } =
    useGetDraftRosterMembersForIncident();
  const { data: positions = [], isPending: positionsPending } =
    useGetPositionsForIncident();
  const { data: seatResources = [], isPending: seatsPending } =
    useGetSeatResourcesForRoster(false);
  const { data: draftSeatResources = [], isPending: draftSeatsPending } =
    useGetSeatResourcesForRoster(true);
  const { data: resourcesInIncident = [], isPending: resourcesPending } =
    useResourcesInIncident();

  const { byPositionAssignment, byPosition, byResourceSet } = useMemo(() => {
    const byPositionAssignmentId = new Map<string, string>();
    const byPositionId = new Map<string, string>();
    const byResourceSetId = new Map<string, string>();

    for (const resource of resourcesInIncident) {
      if (
        resource.position_assignment_id &&
        !byPositionAssignmentId.has(resource.position_assignment_id)
      ) {
        byPositionAssignmentId.set(
          resource.position_assignment_id,
          resource.id
        );
      }
      if (resource.position_id && !byPositionId.has(resource.position_id)) {
        byPositionId.set(resource.position_id, resource.id);
      }
      if (
        resource.resource_set_id &&
        !byResourceSetId.has(resource.resource_set_id)
      ) {
        byResourceSetId.set(resource.resource_set_id, resource.id);
      }
    }

    return {
      byPositionAssignment: byPositionAssignmentId,
      byPosition: byPositionId,
      byResourceSet: byResourceSetId,
    };
  }, [resourcesInIncident]);

  const positionNameById = useMemo(
    () => new Map(positions.map((position) => [position.id, position.name])),
    [positions]
  );

  const occupantsByPosition = useMemo(() => {
    const names = new Map<string, string[]>();

    for (const member of [...activeRoster, ...draftRoster]) {
      const name = member.userName || member.emailAddress || 'Unknown';

      for (const seat of member.incidentPositions ?? []) {
        const held = names.get(seat.positionId) ?? [];
        if (!held.includes(name)) {
          held.push(name);
        }
        names.set(seat.positionId, held);
      }
    }

    return names;
  }, [activeRoster, draftRoster]);

  const options = useMemo<RosterResourceOption[]>(() => {
    const personnel: RosterResourceOption[] = [];
    // A person can hold several seats, and each seat is a separate resource, so
    // rows are one-per-seat and dedupe has to key on the pair.
    const seenPersonSeats = new Set<string>();

    const addPersonnel = (
      roster: typeof activeRoster,
      onDraftOnly: boolean
    ) => {
      for (const member of roster) {
        const name = member.userName || member.emailAddress || 'Unknown';
        const contact =
          member.emailAddress || member.contact?.mobilePhone || null;

        for (const seat of member.incidentPositions ?? []) {
          const dedupeKey = `${member.userId}|${seat.positionId}`;
          if (seenPersonSeats.has(dedupeKey)) continue;
          seenPersonSeats.add(dedupeKey);

          personnel.push({
            key: `personnel-${seat.positionAssignmentId}`,
            label: name,
            positionName: seat.positionName,
            detail: contact,
            kind: 'personnel',
            onDraftOnly,
            resourceInIncidentId:
              byPositionAssignment.get(seat.positionAssignmentId) ?? null,
            positionId: null,
          });
        }
      }
    };

    // Active first, so a seat held on both rosters keeps the resolvable entry
    // and only genuinely draft-only additions are left unavailable.
    addPersonnel(activeRoster, false);
    addPersonnel(draftRoster, true);

    // Planning the seat instead of its occupant is what survives the roster
    // changing under the plan, and it is the only way to plan a seat nobody is
    // sitting in yet. Resource positions are excluded because the resource is the
    // occupant, so they already appear below under their own kind.
    const personnelPositions = positions
      .filter((position) => !position.isResourcePosition)
      .map<RosterResourceOption>((position) => {
        const occupants = occupantsByPosition.get(position.id) ?? [];

        return {
          key: `position-${position.id}`,
          label: position.name,
          positionName: position.name,
          detail:
            occupants.length === 0 ? UNFILLED_SEAT_LABEL : occupants.join(', '),
          kind: 'position',
          onDraftOnly: false,
          resourceInIncidentId: byPosition.get(position.id) ?? null,
          positionId: position.id,
        };
      });

    const resourcePositions = positions
      .filter((position) => position.isResourcePosition)
      .map<RosterResourceOption>((position) => ({
        key: `resource-position-${position.id}`,
        label: position.name,
        positionName: null,
        detail: position.resourceType,
        kind: 'resourcePosition',
        onDraftOnly: false,
        resourceInIncidentId: position.resourceSetId
          ? (byResourceSet.get(position.resourceSetId) ?? null)
          : null,
        positionId: null,
      }));

    const seenResourceSets = new Set(
      seatResources.map((seat) => seat.resourceSetId)
    );
    const seats: RosterResourceOption[] = [
      ...seatResources.map((seat) => ({ seat, onDraftOnly: false })),
      ...draftSeatResources
        .filter((seat) => !seenResourceSets.has(seat.resourceSetId))
        .map((seat) => ({ seat, onDraftOnly: true })),
    ].map(({ seat, onDraftOnly }) => ({
      key: `seat-resource-${seat.id}`,
      label: seat.resourceName || 'Resource',
      positionName: positionNameById.get(seat.positionId) ?? null,
      detail: seat.resourceKind,
      kind: 'seatResource' as const,
      onDraftOnly,
      resourceInIncidentId: byResourceSet.get(seat.resourceSetId) ?? null,
      positionId: null,
    }));

    return [
      ...personnel,
      ...personnelPositions,
      ...resourcePositions,
      ...seats,
    ].sort(byKindThenLabel);
  }, [
    activeRoster,
    draftRoster,
    occupantsByPosition,
    positions,
    seatResources,
    draftSeatResources,
    byPosition,
    byPositionAssignment,
    byResourceSet,
    positionNameById,
  ]);

  return {
    options,
    isPending:
      activePending ||
      draftPending ||
      positionsPending ||
      seatsPending ||
      draftSeatsPending ||
      resourcesPending,
  };
};

export default useRosterResourceOptions;
